import { Button } from '@synoptic/ui-kit/button/button.js';
import { Checkbox } from '@synoptic/ui-kit/checkbox/checkbox.tsx';
import { Input } from '@synoptic/ui-kit/input/input.tsx';
import { useIsTablet } from '@synoptic/ui-kit/responsive/hooks.ts';
import {
  Select,
  SelectContent,
  SelectTrigger,
} from '@synoptic/ui-kit/select/select.tsx';
import { clsx } from 'clsx';
import { LexicalEditor } from 'lexical';
import { FC, useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useNavigation } from 'react-router';
import { StreamType } from '@/__generated__/graphql.ts';
import { categorySelectOptions } from '@/components/streams/stream-categories.ts';
import { pricingToggleItems } from '@/components/streams/stream-form/regular-stream-form/pricing-toggle-items.tsx';
import {
  createStreamFormActions,
  formInfoBlock,
  formInfoDescription,
  formInfoTitle,
  columnFields,
  streamFormActions,
  streamFormTrigger,
  checkbox,
} from '@/components/streams/stream-form/stream-form.css.ts';
import { StreamFormInputs } from '@/components/streams/stream-form/stream-form.tsx';
import { StreamPricingType } from '@/components/streams/stream-pricing-type.tsx';
import { TextEditor } from '@/components/text-editor/text-editor.tsx';
import { stateToText } from '@/components/text-editor/utils/state-to-text.ts';
import { StreamSelectionTagGroup } from '../stream-selection-tag-group/stream-selection-tag-group';

type MainStreamDataStepProps = {
  infoTitle?: string;
  infoDescription?: string;
  hasInitialValues?: boolean;
  setNextStep?: () => void;
  setPrevStep?: () => void;
};

export const MainStreamDataStep: FC<MainStreamDataStepProps> = ({
  hasInitialValues,
  infoTitle,
  infoDescription,
  setNextStep,
  setPrevStep,
}) => {
  const isTablet = useIsTablet();
  const navigation = useNavigation();

  const editorRef = useRef<LexicalEditor | null>(null);

  const { formState, register, control, getValues } =
    useFormContext<StreamFormInputs>();

  return (
    <div className={columnFields}>
      {infoTitle || infoDescription ? (
        <div className={formInfoBlock}>
          {infoTitle ? <h3 className={formInfoTitle}>{infoTitle}</h3> : null}
          {infoDescription ? (
            <p className={formInfoDescription}>{infoDescription}</p>
          ) : null}
        </div>
      ) : null}

      {hasInitialValues &&
      !getValues('paidData') &&
      getValues('streamType') === StreamType.Public ? (
        <Controller
          name={'packageOnly'}
          control={control}
          render={({ field }) => (
            <StreamSelectionTagGroup
              onValueChange={(value) => {
                if (value)
                  field.onChange(value === StreamPricingType.PackageOnly);
              }}
              value={
                field.value
                  ? StreamPricingType.PackageOnly
                  : StreamPricingType.Free
              }
              items={pricingToggleItems.filter(
                (item) => item.value !== StreamPricingType.Paid,
              )}
              label={'Select the type of stream:'}
            />
          )}
        />
      ) : null}

      <Input
        autoFocus={!hasInitialValues}
        {...register('name', {
          required: 'Name is required',
          maxLength: {
            value: 50,
            message: 'Name cannot exceed 50 characters',
          },
          validate: (v) => (v ? v.trim().length > 0 : undefined),
        })}
        maxLength={50}
        errorMessage={formState.errors.name?.message}
        placeholder={'Enter the name here'}
        label={'Name'}
      />

      <Input
        {...register('label', {
          required: 'Label is required',
          maxLength: {
            value: 30,
            message: 'Label cannot exceed 30 characters',
          },
          validate: (v) => v.trim().length > 0,
        })}
        maxLength={30}
        errorMessage={formState.errors.label?.message}
        placeholder={'Enter the label here'}
        label={'Label'}
      />

      <Controller
        name={'streamCategory'}
        control={control}
        rules={{ required: 'Category is required' }}
        render={({ field }) => (
          <Select
            defaultValue={field.value}
            onValueChange={(state) => {
              field.onChange(state);
            }}
          >
            <SelectTrigger
              label={'Category'}
              placeholder={'Select category'}
              errorMessage={formState.errors.streamCategory?.message}
              className={streamFormTrigger}
              variant={'outline'}
            />
            <SelectContent position={'popper'} items={categorySelectOptions} />
          </Select>
        )}
      />

      <Controller
        name={'apiAccessEnabled'}
        control={control}
        render={({ field }) => (
          <label className={checkbox}>
            <Checkbox
              checked={field.value}
              onCheckedChange={(val) => field.onChange(val === true)}
            />
            Allow API access for this stream
          </label>
        )}
      />

      <Controller
        name="descriptionJson"
        control={control}
        rules={{
          validate: () => {
            if (editorRef.current) {
              const textContent = stateToText(
                editorRef.current.getEditorState(),
              ).trim();

              if (textContent.length == 0) {
                return 'Description is required';
              }

              if (textContent.length > 20000) {
                return 'Description cannot exceed 20000 characters';
              }

              return true;
            }
            return false;
          },
        }}
        render={({ field, fieldState }) => {
          return (
            <TextEditor
              initialState={
                field.value
                  ? field.value
                  : formState.defaultValues?.descriptionJson
              }
              namespace={'stream-description'}
              editable
              editorRef={(el) => {
                field.ref(el);
                editorRef.current = el;
              }}
              onChange={(state) => {
                field.onChange(JSON.stringify(state));
              }}
              placeholder="Enter the description here"
              theme="textarea"
              onFocusOutside={field.onBlur}
              rich
              errorMessage={fieldState.error?.message}
              label={'Description'}
            />
          );
        }}
      />

      <div
        className={clsx(
          streamFormActions,
          !hasInitialValues && createStreamFormActions,
        )}
      >
        {setPrevStep ? (
          <Button
            disabled={formState.isSubmitting || navigation.state === 'loading'}
            type={'button'}
            onClick={() => setPrevStep()}
            size={isTablet ? 'large' : 'medium'}
            variant={'secondary'}
          >
            Back
          </Button>
        ) : null}
        <Button
          loading={formState.isSubmitting || navigation.state === 'loading'}
          disabled={!formState.isValid}
          type={setNextStep ? 'button' : 'submit'}
          onClick={() => setNextStep?.()}
          size={isTablet ? 'large' : 'medium'}
        >
          {setNextStep ? 'Next' : hasInitialValues ? 'Save' : 'Create'}
        </Button>
      </div>
    </div>
  );
};
